"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type DoiRow,
  DoiRowCard,
} from "@/components/tools/reference-tools/doi-row";
import { useRun } from "@/components/tools/reference-tools/use-run";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  bestMatch,
  crossrefQueryUrl,
  hasDoi,
  parseCrossrefItems,
  tooShort,
  withDoi,
} from "@/lib/tool-kit/reference-tools/doi-finder";
import { fetchJson, runPool } from "@/lib/tool-kit/reference-tools/network";
import { splitReferences } from "@/lib/tool-kit/reference-tools/references";

/** English strings. A translated content file overrides these through `ui`. */
export const DOI_FINDER_STRINGS = {
  inputLabel:
    "Your reference list — one reference per line, or separated by blank lines",
  inputPlaceholder:
    "Hirsch, J. E. (2005). An index to quantify an individual's scientific research output. Proceedings of the National Academy of Sciences, 102(46), 16569–16572.\nEgghe, L. (2006). Theory and practise of the g-index. Scientometrics, 69(1), 131–152.",
  inputHint:
    "Up to {max} references per run. Each is searched on Crossref, one per second, from your browser.",
  find: "Find DOIs",
  finding: "Searching {done} of {total}…",
  cancel: "Stop",
  emptyPrompt: "Paste a reference list and press Find DOIs.",
  unexpected: "Something went wrong during the search. Please try again.",
  resultsHeading: "Your list with DOIs",
  added: "one:{count} DOI added|other:{count} DOIs added",
  copy: "Copy list",
  copied: "Copied!",
  download: "Download .txt",
  outputNote:
    "Accepted DOIs are added at the end of each reference as https://doi.org/ links, the form APA 7 uses; a database link at the end is replaced by the DOI.",
  matchesHeading: "Reference by reference",
  states: {
    confident: "Confident match",
    uncertain: "Possible match",
    none: "No match",
    hasDoi: "Already has a DOI",
    short: "Too short to search",
    over: "Over the limit",
    failed: "Search failed",
    queued: "Waiting",
  },
  yourReference: "Your reference",
  crossrefRecord: "Crossref record",
  accept: "Add this DOI",
  etAl: "et al.",
  checks: {
    title: "Title words found in your reference: {share}",
    authorYes: "First author ({name}) matches",
    authorNo: "First author ({name}) is not in your reference",
    authorNone: "Crossref lists no author",
    yearExact: "Year matches ({year})",
    yearNear: "Year is one off ({year}) — often online-first vs print",
    yearNo: "Year differs ({year})",
    yearUnknown: "No year to compare",
  },
  notes: {
    none: "No Crossref record matches closely. Many books, reports, theses and older works have no DOI.",
    hasDoi: "Left as it is.",
    short: "Fewer than four words — not enough to search.",
    over: "Run the list again with the remaining references.",
  },
  failures: {
    "rate-limited":
      "Crossref is limiting requests. Wait a minute and try again.",
    unavailable: "Crossref did not answer properly. Try again shortly.",
    timeout: "Crossref took too long to answer. Try again.",
    network: "Crossref could not be reached — check your connection.",
    "not-found": "Crossref returned nothing for this reference.",
  },
} as const;

export type DoiFinderStrings = typeof DOI_FINDER_STRINGS;

const MAX_REFERENCES = 25;
/** Crossref's public pool allows one list query per second. */
const QUERY_INTERVAL_MS = 1100;
const RE_BLANK_LINE = /\n[ \t]*\n/;

function plan(text: string): DoiRow[] {
  let queued = 0;
  return splitReferences(text).map((reference, id): DoiRow => {
    if (hasDoi(reference)) {
      return { id, reference, state: "hasDoi", match: null, accepted: false };
    }
    if (tooShort(reference)) {
      return { id, reference, state: "short", match: null, accepted: false };
    }
    queued += 1;
    const state = queued > MAX_REFERENCES ? "over" : "queued";
    return { id, reference, state, match: null, accepted: false };
  });
}

function outputText(rows: readonly DoiRow[], separator: string): string {
  return rows
    .map((row) =>
      row.state === "done" && row.accepted && row.match
        ? withDoi(row.reference, row.match.candidate.doi).text
        : row.reference
    )
    .join(separator);
}

export default function DoiFinderWidget() {
  const strings = useToolUi(DOI_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [rows, setRows] = useState<DoiRow[]>([]);
  const [failed, setFailed] = useState(false);
  const run = useRun();

  async function handleFind() {
    const planned = plan(text);
    setRows(planned);
    setFailed(false);
    const queue = planned.filter((row) => row.state === "queued");
    const controller = run.start(queue.length);
    const update = (next: DoiRow) => {
      if (run.isCurrent(controller)) {
        setRows((current) =>
          current.map((row) => (row.id === next.id ? next : row))
        );
      }
    };
    try {
      await runPool(
        queue,
        async (row) => {
          const response = await fetchJson<unknown>(
            crossrefQueryUrl(row.reference),
            { signal: controller.signal, retries: 1 }
          );
          if (!response.ok) {
            update({ ...row, state: "failed", reason: response.reason });
            return;
          }
          const match = bestMatch(
            row.reference,
            parseCrossrefItems(response.value)
          );
          update({
            ...row,
            state: "done",
            match,
            accepted: match?.level === "confident",
          });
        },
        {
          concurrency: 1,
          minIntervalMs: QUERY_INTERVAL_MS,
          signal: controller.signal,
          onProgress: (done) => run.advance(controller, done, queue.length),
        }
      );
    } catch {
      if (run.isCurrent(controller)) {
        setFailed(true);
      }
    } finally {
      run.finish(controller);
    }
  }

  function toggle(id: number, accepted: boolean) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, accepted } : row))
    );
  }

  const separator = RE_BLANK_LINE.test(text) ? "\n\n" : "\n";
  const output = useMemo(() => outputText(rows, separator), [rows, separator]);
  const added = rows.filter(
    (row) => row.state === "done" && row.accepted && row.match
  ).length;
  const searched = rows.some((row) => row.state !== "queued");

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: fmt(MAX_REFERENCES) })}
        id="df-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={run.running || text.trim() === ""}
          onClick={handleFind}
          type="button"
        >
          {run.progress
            ? t(strings.finding, {
                done: fmt(run.progress.done),
                total: fmt(run.progress.total),
              })
            : strings.find}
        </button>
        {run.running ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={run.cancel}
            type="button"
          >
            {strings.cancel}
          </button>
        ) : null}
      </div>
      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {rows.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {rows.length > 0 && searched ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.added, locale, added, { count: fmt(added) })}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={output}
                />
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() =>
                    downloadText("references-with-dois.txt", output)
                  }
                  type="button"
                >
                  {strings.download}
                </button>
              </div>
            </div>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
              {output}
            </pre>
            <p className={HINT}>{strings.outputNote}</p>
          </div>
        ) : null}
        {rows.length > 0 ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.matchesHeading}</h3>
            <ol className="space-y-3">
              {rows.map((row) => (
                <DoiRowCard
                  key={row.id}
                  onToggle={toggle}
                  row={row}
                  strings={strings}
                />
              ))}
            </ol>
          </div>
        ) : null}
      </div>
    </section>
  );
}
